import { Select as SelectPrimitive, type SelectRootItemComponentProps } from "@kobalte/core/select";
import { Check, ChevronDown, Plus } from "@fleet/icons";
import { createMemo, type JSX, Show, splitProps } from "solid-js";
import { cn } from "@mealplan/shared";

/**
 * Renderer for each option. Defined at module level (not inside `Select`)
 * to avoid creating a new component on every render — the pattern Kobalte expects
 * via the `itemComponent` prop and that satisfies `no-unstable-nested-components`.
 */
function SelectOption(props: SelectRootItemComponentProps<string>) {
  return (
    <SelectPrimitive.Item
      item={props.item}
      class={cn(
        "relative flex cursor-pointer select-none items-center justify-between rounded-md px-3 py-2 text-sm outline-none",
        "transition-colors duration-100",
        "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      )}
    >
      <SelectPrimitive.ItemLabel>{props.item.rawValue}</SelectPrimitive.ItemLabel>
      <SelectPrimitive.ItemIndicator class="flex h-4 w-4 items-center justify-center">
        <Check class="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

/**
 * Premium Select built on `@kobalte/core` — same pattern/style as
 * `popover.tsx` / `dialog.tsx` / `checkbox.tsx`. Repo tokens, animated
 * panel, factory keyboard + typeahead + ARIA, and theme-safe
 * (accent/accent-foreground pairs).
 *
 * Simple API based on `options: string[]` for product dropdowns
 * (units, category/aisle, servings). For advanced cases, use the
 * Kobalte primitives re-exported at the end of the file.
 */
/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true.*/
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph.*/
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No recipes yet"). */
  description?: string;
}

export interface SelectProps {
  options: readonly string[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  /** Accessible text when there is no visible <Label>. */
  "aria-label"?: string;
  class?: string;
  /** Class applied to the trigger (button). */
  triggerClass?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  "data-testid"?: string;
  /**
   * Shown inside the dropdown when `options` is empty. Lets the user jump to the
   * place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA.*/
  emptyLabel?: string;
}

export function Select(props: SelectProps) {
  const [local] = splitProps(props, [
    "options",
    "value",
    "onChange",
    "placeholder",
    "aria-label",
    "class",
    "triggerClass",
    "disabled",
    "id",
    "name",
    "data-testid",
    "emptyAction",
    "emptyLabel",
  ]);

  // ── CC-26: STABLE identity for the options (CC-23 recipe within Select itself).
  // PowerSync LIVE queries re-emit arrays with NEW identity on every tick
  // even when their contents do not change; call sites usually feed
  // `options` with an inline `.map()`/spread (new identity per render). Kobalte
  // rebuilds its collection from that identity and its Listbox re-runs
  // `renderItem` per node → with the listbox OPEN the DOM of each option is
  // RE-MOUNTED under the pointer and the click lands on a stale node: the selection
  // is discarded (empty value, submit disabled).
  //
  // Here `options` is `string[]` (not the object {value,label} API of the canonical
  // field-check), so the identity to collapse is that of the ARRAY, not each
  // object: strings are already stable by value and `reconcileById` (by-id over
  // objects) does not apply. We reuse the previous array when its content has not
  // changed, so Kobalte's collection is not rebuilt and the click persists.
  // Guarded by components/ui/__tests__/select-live-options.test.tsx.
  const stableOptions = createMemo<string[]>((prev) => {
    const next = local.options;
    if (
      prev &&
      prev.length === next.length &&
      prev.every((value, i) => Object.is(value, next[i]))
    ) {
      return prev;
    }
    return [...next];
  });

  const isEmpty = () => stableOptions().length === 0;

  return (
    <SelectPrimitive<string>
      options={stableOptions()}
      value={local.value ?? null}
      // Required single-select: re-clicking the already selected option must NOT
      // deselect it (Kobalte's toggle would emit null and clear the field
      // before submit). It can change to another option, but not be emptied by re-clicking.
      disallowEmptySelection
      onChange={(value) => local.onChange?.(value)}
      disabled={local.disabled}
      placeholder={local.placeholder}
      gutter={6}
      sameWidth
      class={local.class}
      itemComponent={SelectOption}
    >
      {/*
        Hermano justo ANTES del Trigger, nunca dentro: el Trigger renderiza un
        <button>, y anidar el <select> nativo de HiddenSelect ahí violaría el
        modelo de contenido de HTML5 (contenido interactivo dentro de contenido
        interactivo). `name` se reenvía SIEMPRE — sin `<Show when={local.name}>`,
        que dejaba el reenvío inerte cuando el valor llegaba más tarde que el
        primer render (la guarda decide en el montaje inicial y Kobalte no
        vuelve a montar el HiddenSelect si `local.name` pasa de vacío a un
        string).
      */}
      <SelectPrimitive.HiddenSelect name={local.name} />
      <SelectPrimitive.Trigger
        id={local.id}
        aria-label={local["aria-label"]}
        data-testid={local["data-testid"]}
        class={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
          "transition-colors duration-100 hover:border-accent/50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "data-[expanded]:border-accent/60",
          local.triggerClass,
        )}
      >
        <SelectPrimitive.Value<string> class="block truncate text-left data-[placeholder-shown]:text-muted-foreground">
          {(state) => state.selectedOption()}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon class="shrink-0 text-muted-foreground transition-transform duration-200 data-[expanded]:rotate-180">
          <ChevronDown class="h-4 w-4" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          class={cn(
            "z-[220] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg outline-none",
            "ui-select-content",
          )}
        >
          <Show
            when={!isEmpty()}
            fallback={<SelectEmptyContent action={local.emptyAction} label={local.emptyLabel} />}
          >
            <SelectPrimitive.Listbox class="max-h-60 overflow-y-auto p-1" />
          </Show>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm font-medium outline-none",
    "text-popover-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-2.5 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-2.5 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}
